import type { ReactNode } from 'react';
import { Avatar, Flex, HStack, Icon, Link, Stack, Text } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { ExternalLink, Mail } from 'lucide-react';
import { CopyToClipboard } from '@duke-hq/ui/components/CopyToClipboard/CopyToClipboard';

type TicketHeaderProps = {
  requesterName: string;
  requesterType: string;
  requesterEmail: string;
  requesterTypeIcon?: ReactNode;
  requesterLink?: string;
  actions?: ReactNode;
};

export function TicketHeader({
  requesterName,
  requesterType,
  requesterEmail,
  requesterTypeIcon,
  requesterLink,
  actions,
}: TicketHeaderProps) {
  return (
    <Flex
      gap="4"
      bg="bg.subtle"
      px={3}
      minH="12"
      py="2"
      borderBottom="1px"
      borderColor="border.default"
      align="center"
      justify="space-between"
    >
      <HStack spacing="3" flex="1" minW={0} fontSize="xs">
        <Avatar size="xs" name={requesterName} />

        <Stack spacing="0" minW={0}>
          {requesterLink !== undefined ? (
            <Link
              as={RouterLink}
              to={requesterLink}
              target="_blank"
              rel="noopener noreferrer"
              fontWeight="semibold"
              color="brand.500"
              width="fit-content"
              display="inline-flex"
              alignItems="center"
              gap="1"
              _hover={{ textDecoration: 'underline', color: 'brand.700' }}
            >
              {requesterName}
              <Icon as={ExternalLink} boxSize="3" />
            </Link>
          ) : (
            <Text fontWeight="semibold" lineHeight="1.2" noOfLines={1}>
              {requesterName}
            </Text>
          )}

          <HStack spacing="1" align="center" minW={0}>
            {requesterTypeIcon}

            <Text fontSize="sm" color="fg.subtle" marginRight="2" flexShrink={0}>
              {requesterType}
            </Text>

            {requesterEmail && (
              <>
                <Icon as={Mail} boxSize="3.5" color="fg.muted" flexShrink={0} />
                <CopyToClipboard value={requesterEmail} label="Copy email" revealOnHover>
                  <Text fontSize="sm" color="fg.muted" noOfLines={1}>
                    {requesterEmail}
                  </Text>
                </CopyToClipboard>
              </>
            )}
          </HStack>
        </Stack>
      </HStack>

      {actions}
    </Flex>
  );
}

export default TicketHeader;
